/**
 * Adaptive quality governor (docs/04). Pure state machine fed with frame timestamps, so it is
 * unit-tested with synthetic timelines.
 *
 * - Detects the display refresh rate from the median callback interval.
 * - Paces rendering to the target frame rate as an even divisor of the refresh rate (steady 60 on a
 *   120 Hz screen looks better than a jittery 90).
 * - Lowers the render scale quickly when frames are missed and raises it slowly when there is
 *   headroom, with exponential back-off when an upgrade immediately causes misses.
 */
import type { TargetFps } from '../../shared/settings';

export interface GovernorConfig {
  adaptive: boolean;
  targetFps: TargetFps;
  scaleMin: number;
  scaleMax: number;
}

const COMMON_REFRESH = [24, 30, 48, 50, 60, 72, 75, 90, 100, 120, 144, 165, 240];
const DOWN_DELAY_MS = 500;
const UP_DELAY_MS = 4000;
const MAX_UP_DELAY_MS = 30000;
const DOWN_FACTOR = 0.85;
const UP_FACTOR = 1.1;
const MISS_RATIO_DOWN = 0.1;
const HITCH_MS = 250;
const WINDOW_MS = 1000;
const MAX_TIER = 3;

export class Governor {
  scale = 1;
  refreshHz = 60;
  divisor = 1;
  lastReason = 'starting';
  /** Effect quality tier: 0 = all effects, 3 = cheapest (see POSTFX dropAtTier). */
  tier = 0;

  private cfg: GovernorConfig;
  private readonly tickDeltas = new Float64Array(90);
  private tickPos = 0;
  private tickCount = 0;
  private lastTick = -1;
  private ticks = 0;
  private lastRendered = -1;
  private readonly frameTimes = new Float64Array(256).fill(-Infinity);
  private readonly missTimes = new Float64Array(256).fill(-Infinity);
  private framePos = 0;
  private missPos = 0;
  private lastChange = 0;
  private lastUpgrade = -Infinity;
  private upDelay = UP_DELAY_MS;
  private lastMiss = -Infinity;
  private cpuMs = 0;

  constructor(cfg: GovernorConfig) {
    this.cfg = cfg;
    this.scale = cfg.scaleMax;
  }

  configure(cfg: GovernorConfig): void {
    this.cfg = cfg;
    if (!cfg.adaptive) {
      this.scale = cfg.scaleMax;
      this.tier = 0;
      this.lastReason = 'adaptive off';
    } else {
      this.scale = Math.min(cfg.scaleMax, Math.max(cfg.scaleMin, this.scale));
    }
    this.updateDivisor();
  }

  get targetHz(): number {
    return this.refreshHz / this.divisor;
  }

  /** Call on every animation callback. Returns true when a frame should be rendered now. */
  tick(now: number): boolean {
    if (this.lastTick >= 0) {
      const d = now - this.lastTick;
      if (d > 0 && d < HITCH_MS) {
        this.tickDeltas[this.tickPos] = d;
        this.tickPos = (this.tickPos + 1) % this.tickDeltas.length;
        if (this.tickCount < this.tickDeltas.length) this.tickCount++;
        if (this.tickCount >= 30 && this.tickPos % 30 === 0) this.estimateRefresh();
      }
    }
    this.lastTick = now;
    return this.ticks++ % this.divisor === 0;
  }

  /** Call after rendering a frame. `cpuMs` is the renderer's own CPU time for that frame. */
  frameDone(now: number, cpuMs: number): void {
    this.cpuMs += 0.1 * (cpuMs - this.cpuMs);
    if (this.lastRendered >= 0) {
      const interval = now - this.lastRendered;
      const expected = (1000 / this.refreshHz) * this.divisor;
      if (interval > HITCH_MS) {
        // Tab hidden, debugger, output reconfigured: not a performance signal.
        this.lastChange = now;
      } else {
        this.frameTimes[this.framePos] = now;
        this.framePos = (this.framePos + 1) % this.frameTimes.length;
        if (interval > expected * 1.5) {
          this.missTimes[this.missPos] = now;
          this.missPos = (this.missPos + 1) % this.missTimes.length;
          this.lastMiss = now;
        }
      }
    }
    this.lastRendered = now;
    if (this.cfg.adaptive) this.decide(now);
  }

  private countSince(buf: Float64Array, since: number): number {
    let n = 0;
    for (let i = 0; i < buf.length; i++) if (buf[i]! > since) n++;
    return n;
  }

  private decide(now: number): void {
    const { scaleMin, scaleMax } = this.cfg;
    const frames = this.countSince(this.frameTimes, now - WINDOW_MS);
    const misses = this.countSince(this.missTimes, now - WINDOW_MS);
    const missRatio = frames > 0 ? misses / frames : 0;
    const expected = (1000 / this.refreshHz) * this.divisor;

    if (missRatio > MISS_RATIO_DOWN && now - this.lastChange > DOWN_DELAY_MS) {
      if (now - this.lastUpgrade < 2000) this.upDelay = Math.min(MAX_UP_DELAY_MS, this.upDelay * 2);
      const pct = `${(missRatio * 100).toFixed(0)}% frames late`;
      // Resolution first; once it is at the floor, drop effect tiers (lens → bloom → feedback).
      if (this.scale > scaleMin + 1e-6) {
        this.scale = Math.max(scaleMin, this.scale * DOWN_FACTOR);
        this.lastReason = `↓ scale · ${pct}`;
      } else if (this.tier < MAX_TIER) {
        this.tier++;
        this.lastReason = `↓ effects tier ${this.tier} · ${pct}`;
      } else {
        return;
      }
      this.lastChange = now;
      return;
    }
    const recovered =
      now - this.lastMiss > this.upDelay &&
      now - this.lastChange > this.upDelay &&
      this.cpuMs < expected * 0.5;
    if (!recovered) return;
    // Recover in reverse order: effects back before resolution.
    if (this.tier > 0) {
      this.tier--;
      this.lastReason = `↑ effects tier ${this.tier}`;
    } else if (this.scale < scaleMax) {
      this.scale = Math.min(scaleMax, this.scale * UP_FACTOR);
      this.lastReason = '↑ headroom';
    } else {
      return;
    }
    this.lastChange = now;
    this.lastUpgrade = now;
  }

  private estimateRefresh(): void {
    const sorted = Array.from(this.tickDeltas.subarray(0, this.tickCount)).sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)]!;
    let hz = 1000 / median;
    for (const c of COMMON_REFRESH) if (Math.abs(hz - c) / c < 0.04) hz = c;
    if (Math.abs(hz - this.refreshHz) / this.refreshHz > 0.02) {
      this.refreshHz = hz;
      this.updateDivisor();
    }
  }

  private updateDivisor(): void {
    const target = this.cfg.targetFps === 'auto' ? this.refreshHz : this.cfg.targetFps;
    this.divisor = Math.max(1, Math.round(this.refreshHz / Math.min(target, this.refreshHz)));
  }

  get status(): string {
    return this.cfg.adaptive ? this.lastReason : 'adaptive off';
  }
}
